import { Template } from "@/lib/api";
import { AUTO_IMAGE_MODEL_ID, getModelById } from "@/lib/model-helpers";

export const AUTO_VIDEO_MODEL_ID = "auto-video";

export type TemplateShelfId =
  | "portraits"
  | "women"
  | "men"
  | "couples"
  | "fashion"
  | "cinematic"
  | "illustration"
  | "products"
  | "effects";

export interface TemplateShelfDefinition {
  id: TemplateShelfId;
  label: string;
  description: string;
}

export interface TemplateLibraryMeta {
  isPromptHero: boolean;
  originUrl: string | null;
  modelLabel: string | null;
  category: string | null;
  subject: string | null;
  tags: string[];
  collection: string | null;
}

const CATEGORY_FALLBACKS: Record<string, string> = {
  portrait: "portrait",
  portraits: "portrait",
  fashion: "fashion",
  editorial: "fashion",
  cinematic: "cinematic",
  film: "cinematic",
  illustration: "illustration",
  anime: "illustration",
  product: "products",
  products: "products",
};

const SUBJECT_FALLBACKS: Record<string, string> = {
  women: "women",
  woman: "women",
  girls: "women",
  girl: "women",
  men: "men",
  man: "men",
  male: "men",
  couples: "couples",
  couple: "couples",
  product: "products",
  products: "products",
};

export const TEMPLATE_SHELVES: TemplateShelfDefinition[] = [
  { id: "portraits", label: "Портреты", description: "Идеи для портретов, бьюти и лайфстайл-съёмок" },
  { id: "women", label: "Женские образы", description: "Женские портреты и модные референсы" },
  { id: "men", label: "Мужские образы", description: "Мужские фотосеты и стритстайл" },
  { id: "couples", label: "Пары и истории", description: "Сцены с парой, эмоции и сюжетный кадр" },
  { id: "fashion", label: "Мода", description: "Подиум, стайлинг, модная съёмка" },
  { id: "cinematic", label: "Кино", description: "Драматичный свет, 35мм, киноязык" },
  { id: "illustration", label: "Иллюстрации", description: "Аниме, 3D, мультяшный стиль, диджитал-арт" },
  { id: "products", label: "Продукты", description: "Предметная съёмка и визуалы для брендов" },
  { id: "effects", label: "Эффекты Higgsfield 🔥", description: "Видеоэффекты — загрузите фото и получите результат" },
];

function normalizeText(value: string): string {
  return ` ${String(value || "").toLowerCase()} `;
}

function getTemplatePromptText(template: Template): string {
  return [template.title, template.description, template.prompt?.base]
    .filter(Boolean)
    .join(" ");
}

function getDefaultModelLabel(template: Template): string | null {
  if (!template.default_model) return null;
  return getModelById(template.default_model)?.name || template.default_model;
}

export function isPromptHeroTemplate(template: Pick<Template, "id" | "source">): boolean {
  return (
    template.id.startsWith("prompthero-") ||
    (template.source || "").toLowerCase().includes("prompthero")
  );
}

export function isPromptLibraryIdeaTemplate(template: Pick<Template, "id" | "source" | "type">): boolean {
  return template.source !== "external" || template.type === "motion";
}

export function getTemplateLibraryMeta(template: Template): TemplateLibraryMeta {
  const defaults = template.defaults || {};
  const promptText = normalizeText(getTemplatePromptText(template));
  const tags = Array.isArray(defaults.library_tags)
    ? defaults.library_tags.filter((value): value is string => typeof value === "string")
    : [];
  const normalizedCategory =
    typeof defaults.library_category === "string"
      ? CATEGORY_FALLBACKS[defaults.library_category] || defaults.library_category
      : null;
  const normalizedSubject =
    typeof defaults.library_subject === "string"
      ? SUBJECT_FALLBACKS[defaults.library_subject] || defaults.library_subject
      : null;

  let category = normalizedCategory;
  if (!category) {
    if (promptText.includes(" anime ") || promptText.includes(" pixar ") || promptText.includes(" illustration ")) {
      category = "illustration";
    } else if (promptText.includes(" fashion ") || promptText.includes(" editorial ") || promptText.includes(" runway ")) {
      category = "fashion";
    } else if (promptText.includes(" cinematic ") || promptText.includes(" 35mm ") || promptText.includes(" kodak ")) {
      category = "cinematic";
    } else if (promptText.includes(" product ") || promptText.includes(" perfume ") || promptText.includes(" bottle ")) {
      category = "products";
    } else {
      category = "portrait";
    }
  }

  let subject = normalizedSubject;
  if (!subject) {
    if (promptText.includes(" couple ") || promptText.includes(" hugging ")) {
      subject = "couples";
    } else if (
      promptText.includes(" woman ") ||
      promptText.includes(" women ") ||
      promptText.includes(" girl ")
    ) {
      subject = "women";
    } else if (promptText.includes(" man ") || promptText.includes(" men ") || promptText.includes(" male ")) {
      subject = "men";
    } else if (category === "products") {
      subject = "products";
    } else {
      subject = null;
    }
  }

  return {
    isPromptHero: isPromptHeroTemplate(template),
    originUrl: typeof defaults.prompt_url === "string" ? defaults.prompt_url : null,
    modelLabel: getDefaultModelLabel(template),
    category,
    subject,
    tags,
    collection: typeof defaults.library_collection === "string" ? defaults.library_collection : null,
  };
}

export function getTemplatePrimaryShelf(template: Template): TemplateShelfId {
  // Motion/effect templates go to effects shelf
  if ((template.type || "").toLowerCase() === "motion") return "effects";

  const meta = getTemplateLibraryMeta(template);

  if (meta.subject === "couples") return "couples";
  if (meta.subject === "women") return "women";
  if (meta.subject === "men") return "men";
  if (meta.subject === "products") return "products";
  if (meta.category === "fashion") return "fashion";
  if (meta.category === "illustration") return "illustration";
  if (meta.category === "cinematic") return "cinematic";
  return "portraits";
}

export function getTemplateSecondaryTags(template: Template): string[] {
  const meta = getTemplateLibraryMeta(template);
  const tags = new Set<string>();
  if (meta.category === "fashion") tags.add("Fashion");
  if (meta.category === "cinematic") tags.add("Cinematic");
  if (meta.category === "illustration") tags.add("Illustration");
  if (meta.subject === "women") tags.add("Women");
  if (meta.subject === "men") tags.add("Men");
  if (meta.subject === "couples") tags.add("Couples");
  for (const value of meta.tags.slice(0, 2)) {
    tags.add(value.charAt(0).toUpperCase() + value.slice(1));
  }
  return Array.from(tags).slice(0, 3);
}

export function resolveTemplateCreateMode(template: { type?: string | null; default_model?: string | null; id?: string | null }): string {
  const normalizedType = (template.type || "").toLowerCase();
  if (normalizedType === "video") return "video";
  if (normalizedType === "photoshoot") return "photoshoot";
  // Motion effects → route to the appropriate tool panel
  if (normalizedType === "motion") {
    const model = (template.default_model || "").toLowerCase();
    const id = (template.id || "").toLowerCase();
    if (model.includes("babydance") || id.includes("babydance")) return "babydance";
    if (model.includes("motion-flow") || model.includes("motionflow") || id.includes("motionflow")) return "motionflow";
    // Default: Higgsfield/WaveSpeed motion effects use ToolModePanel via motionflow
    return "motionflow";
  }
  return "image";
}

export function resolveTemplatePreferredModelId(
  template: {
    type?: string | null;
    default_model?: string | null;
    model_id?: string | null;
    allowed_models?: string[] | null;
  }
): string | undefined {
  const mode = resolveTemplateCreateMode(template);
  const preferredTypes = mode === "video" ? new Set(["video"]) : new Set(["image", "chat"]);
  const candidates = [
    template.default_model,
    template.model_id,
    ...(Array.isArray(template.allowed_models) ? template.allowed_models : []),
  ].filter((value): value is string => typeof value === "string" && value.length > 0);

  for (const candidate of candidates) {
    const model = getModelById(candidate);
    if (!model) continue;
    if (preferredTypes.has(model.type)) {
      return candidate;
    }
  }

  for (const candidate of candidates) {
    if (getModelById(candidate)) {
      return candidate;
    }
  }

  if (mode === "video") return AUTO_VIDEO_MODEL_ID;
  if (mode === "image") return AUTO_IMAGE_MODEL_ID;
  return undefined;
}
